<template>
  <div class="email-container">
    <div class="email-content">
      <vue-email ref="emailer"
        v-if="!isImageAttachActive && !isDocAttachActive"
        :value="emailer"
        :asset-id="assetId"
        :quote-image-attachments="selectedImages"
        :quote-attachments="selectedDocuments"
        :reply-to-message-id="replyToMessageId"
        :to-emails="replyToSenderEmails"
        :cc-emails="replyToEmailCcs"
        :bcc-emails="replyToEmailBccs"
        :subject="emailSubject"
        :message="emailMessage"
        :is-show-cc-bcc="(!!replyToEmailCcs.length || !!replyToEmailBccs.length)"
        @new-email="newEmailInput"
        @attachments-updated="handleAttachmentsUpdate"
        @validation-changed="handleValidationChanged"
        @input="$emit('update:emailer', $event)"
        @sent="sent" />

      <!-- Image Selection Modal -->
      <base-modal-ex :active="isImageAttachActive"
        :has-modal-card="true"
        animation-in="fadeIn"
        animation-out="fadeOut"
        :min-width="60"
        @close="closeImageModal">
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Select Images</p>
          </header>
          <section class="modal-card-body">
            <image-select v-model="selectedImages"
              :quote-id="assetId"
              :quote-ids="computeQuoteIds"
              :subtitle="`Select images to attach`"
              @input="onImagesSelected" />
          </section>
          <footer class="modal-card-foot is-justify-content-flex-end">
            <button class="button"
              @click="closeImageModal">
              Cancel
            </button>
            <button class="button is-success"
              @click="confirmImageSelection">
              Done
            </button>
          </footer>
        </div>
      </base-modal-ex>

      <!-- Document Selection Modal -->
      <base-modal-ex :active="isDocAttachActive"
        :has-modal-card="true"
        :min-width="60"
        animation-in="fadeIn"
        animation-out="fadeOut"
        @close="closeDocModal">
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Select Documents</p>
          </header>
          <section class="modal-card-body">
            <attachment-select v-model="selectedDocuments"
              :quote-id="assetId"
              :subtitle="`Select documents to attach`"
              @input="onDocumentsSelected" />
          </section>
          <footer class="modal-card-foot is-justify-content-flex-end">
            <button class="button"
              @click="closeDocModal">
              Cancel
            </button>
            <button class="button is-success"
              @click="confirmDocSelection">
              Done
            </button>
          </footer>
        </div>
      </base-modal-ex>
    </div>
    <div class="email-actions">
      <slot name="template-actions" />
      <div class="buttons">
        <button class="button is-warning tooltip is-tooltip-topright"
          @click="resetEmail"
          :disabled="!enableSend || isImageAttachActive || isDocAttachActive"
          data-tooltip="Reset Email">
          <span class="icon">
            <i class="mdi mdi-refresh mdi-18px" />
          </span>
          <span>Reset</span>
        </button>
        <div class="dropdown"
          v-if="enableAttach"
          :class="{ 'is-active': showAttachDropdown }"
          style="display: inline-block;">
          <button class="button is-primary"
            @click="toggleAttachDropdown">
            <span class="icon">
              <i class="mdi mdi-paperclip" />
            </span>
            <!-- <span>Attach</span> -->
          </button>
          <div class="dropdown-menu"
            role="menu"
            style="left: -120px; right: auto; top: auto; bottom: 100%; transform: translateY(-8px);">
            <div style="position: absolute; left: 130px; bottom: -8px; width: 0; height: 0; border-left: 8px solid transparent; border-right: 8px solid transparent; border-top: 8px solid #fff; z-index: 2;" />
            <div class="dropdown-content">
              <a class="dropdown-item"
                @click="showAttachImage">
                <span class="icon">
                  <i class="mdi mdi-image-multiple" />
                </span>
                Images
              </a>
              <a class="dropdown-item"
                @click="attachDocs">
                <span class="icon">
                  <i class="mdi mdi-file-document-outline" />
                </span>
                Documents
              </a>
            </div>
          </div>
        </div>
        <button class="button is-success"
          @click="sendEmail"
          :disabled="!enableSend || isImageAttachActive || isDocAttachActive">
          <span class="icon">
            <i class="mdi mdi-send" />
          </span>
          <span>Send</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { VueEmail } from '@/components/VueEmail'
import ImageSelect from '@/components/ImageSelect'
import AttachmentSelect from '@/views/quote/components/AttachmentSelect.vue'
import BaseModalEx from '@/components/BulmaModal/BaseModalEx.vue'
import { Emailer } from '@/classes'
import _cloneDeep from 'lodash/cloneDeep'
import StoreUtil from '@/store/utils'
import QuoteCommunicationsMixin from '../mixins/QuoteCommunicationsMixin'

const divider = '=============================================================='
const copyright =
  'All accompanying materials are the exclusive property of [COMPANY].  All [COMPANY] rights are reserved.  These Materials must not be reproduced, adapted, modified, published, or communicated to any person in any form, without express prior written consent of [COMPANY].  Any unauthorised use of these Materials will be vigorously pursued and may result in legal action, including a claim for damages or an account for loss of profits.'

export default {
  name: 'QuoteEmailModal',
  components: {
    VueEmail,
    ImageSelect,
    AttachmentSelect,
    BaseModalEx
  },
  mixins: [QuoteCommunicationsMixin],
  props: {
    active: {
      type: Boolean,
      default: false
    },
    emailer: {
      type: Object,
      required: true
    },
    quoteIds: {
      type: Array,
      default: () => []
    },
    enableAttach: {
      type: Boolean,
      default: true
    },
    replyToMessageId: {
      type: String,
      default: ''
    },
    replyToSenderEmails: {
      type: Array,
      default: () => []
    },
    replyToEmailCcs: {
      type: Array,
      default: () => []
    },
    replyToEmailBccs: {
      type: Array,
      default: () => []
    },
    emailSubject: {
      type: String,
      default: ''
    },
    emailMessage: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isImageAttachActive: false,
      isDocAttachActive: false,
      isEnteringNewEmail: false,
      selectedImages: [],
      selectedImageIds: [],
      selectedDocuments: [],
      showAttachDropdown: false,
      allowedImageExtensions: ['.jpg', '.jpeg', '.png'],
      allowedDocumentExtensions: ['.pdf'],
      emailValidation: {
        canSend: true,
        isAttachmentSizeExceeded: false,
        totalSize: 0,
        errors: []
      }
    }
  },
  computed: {
    enableSend() {
      return !this.isEnteringNewEmail && this.emailValidation.canSend
    },
    assetId() {
      return this.emailer.assetId
    },
    computeQuoteIds() {
      const ids = this.emailer.quoteIds
      return Array.isArray(ids) ? ids : []
    }
  },
  created() {
    this.clearSessionStorage()
  },
  mounted() {
    this.resetEmailer()
  },
  beforeDestroy() {
    this.cleanEmailer()
  },
  methods: {
    async sendEmail() {
      await this.$refs.emailer.send()
      //this.clearSelectedTemplate()
    },
    resetEmail() {
      this.discardEmail()
      this.$emit('resetEmail')
    },
    discardEmail() {
      this.isImageAttachActive = false
      this.isDocAttachActive = false
      this.selectedImageIds.splice(0, this.selectedImageIds.length)
      this.selectedImages.splice(0, this.selectedImages.length)
      this.selectedDocuments.splice(0, this.selectedDocuments.length)
      this.resetEmailer()
      this.$emit('update:active', false)
      this.$emit('discard')
    },
    toggleAttachDropdown() {
      this.showAttachDropdown = !this.showAttachDropdown
    },
    onFileLabelClick() {
      this.$refs.fileInput.accept = this.allowedImageExtensions.join(',') + ',' + this.allowedDocumentExtensions.join(',')
      this.$refs.fileInput.click()
    },
    async onFileChange(event) {
      const files = Array.from(event.target.files)
      await this.onAttachFiles(files)
      event.target.value = '' // Clear input after files are processed
      this.showAttachDropdown = false
    },
    showAttachImage() {
      this.isImageAttachActive = true
      this.isDocAttachActive = false
      // Initialize selectedImages with current quoteImageAttachments if any
      this.selectedImages = this.emailer.quoteImageAttachments ? [...this.emailer.quoteImageAttachments] : []
    },
    closeImageModal() {
      this.isImageAttachActive = false
      this.showAttachDropdown = false
      // Reset selections if canceled - restore from emailer
      this.selectedImages = this.emailer.quoteImageAttachments ? [...this.emailer.quoteImageAttachments] : []
    },
    onImagesSelected(selectedImages) {
      this.selectedImages = selectedImages
      console.log('Selected Images:', this.selectedImages)
      //this.$emit('update:selectedImages', this.selectedImages)
      //this.updateAttachmentList()
    },
    confirmImageSelection() {
      const updatedEmailer = _cloneDeep(this.emailer)
      if (!updatedEmailer.quoteImageAttachments) {
        this.$set(updatedEmailer, 'quoteImageAttachments', [])
      }
      updatedEmailer.quoteImageAttachments = this.selectedImages
      this.$emit('update:emailer', updatedEmailer)
      this.isImageAttachActive = false
      this.showAttachDropdown = false
      //this.updateAttachmentList()
    },
    attachDocs() {
      this.isDocAttachActive = true
      this.isImageAttachActive = false
      // Initialize selectedDocuments with current quoteAttachments if any
      this.selectedDocuments = this.emailer.quoteAttachments ? [...this.emailer.quoteAttachments] : []
    },
    closeDocModal() {
      this.isDocAttachActive = false
      this.showAttachDropdown = false
      // Reset selections if canceled - restore from emailer
      this.selectedDocuments = this.emailer.quoteAttachments ? [...this.emailer.quoteAttachments] : []
    },
    confirmDocSelection() {
      const updatedEmailer = _cloneDeep(this.emailer)
      if (!updatedEmailer.quoteAttachments) {
        this.$set(updatedEmailer, 'quoteAttachments', [])
      }
      updatedEmailer.quoteAttachments = this.selectedDocuments
      this.$emit('update:emailer', updatedEmailer)
      this.isDocAttachActive = false
      this.showAttachDropdown = false
    },
    async onAttachFiles(files) {
      const fileArray = Array.from(files)
      const updatedEmailer = _cloneDeep(this.emailer)

      if (!updatedEmailer.customAttachments) {
        this.$set(updatedEmailer, 'customAttachments', [])
      }

      const maxSizeMB = 5
      const maxSizeBytes = maxSizeMB * 1024 * 1024

      // Define allowed file types
      const allowedExtensions = [...this.allowedImageExtensions, ...this.allowedDocumentExtensions]

      const oversizedFiles = []
      const invalidTypeFiles = []
      const validFiles = []

      // Check file sizes and types
      for (const file of fileArray) {
        const fileName = file.name.toLowerCase()
        const fileExtension = fileName.substring(fileName.lastIndexOf('.'))

        // Check file type
        if (!allowedExtensions.includes(fileExtension)) {
          invalidTypeFiles.push(file.name)
          continue
        }

        // Check file size
        if (file.size > maxSizeBytes) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2)
          oversizedFiles.push(`${file.name} (${fileSizeMB}MB)`)
        } else {
          validFiles.push(file)
        }
      }

      // Show error for invalid file types
      if (invalidTypeFiles.length > 0) {
        const fileList = invalidTypeFiles.join(', ')
        const allowedTypes = 'Images (jpg, jpeg, png) and Documents (pdf)'
        this.$notification.error(
          'Invalid File Type',
          `The following files have unsupported file types and cannot be attached: ${fileList}. Only ${allowedTypes} are allowed.`
        )
      }

      // Show error for oversized files
      if (oversizedFiles.length > 0) {
        const fileList = oversizedFiles.join(', ')
        this.$notification.error('File Size Error', `The following files exceed the ${maxSizeMB}MB size limit and cannot be attached: ${fileList}`)
      }

      // Process only valid files
      for (const file of validFiles) {
        // Read file as ArrayBuffer (binary)
        const arrayBuffer = await file.arrayBuffer()
        const fileSizeMB = (arrayBuffer.byteLength / (1024 * 1024)).toFixed(2)
        const fileObj = {
          fileName: file.name,
          fileBytes: arrayBuffer,
          fileType: file.type,
          fileSizeMB: parseFloat(fileSizeMB)
        }
        // Add to customAttachments
        updatedEmailer.customAttachments.push(fileObj)
      }

      this.$emit('update:emailer', updatedEmailer)
    },

    onDocumentsSelected(docs) {
      this.selectedDocuments = Array.isArray(docs) ? docs : []
      this.$emit('update:selectedDocuments', this.selectedDocuments)
      //this.updateAttachmentList()
    },

    handleAttachmentsUpdate(updatedAttachments) {
      // Update selectedDocuments to match the current attachments
      this.selectedDocuments = updatedAttachments || []
      // Emit the update event
      this.$emit('update:selectedDocuments', this.selectedDocuments)
      // Update the attachment list to ensure consistency
      //this.updateAttachmentList()
    },
    handleValidationChanged(validation) {
      this.emailValidation = validation
    },
    newEmailInput(newEmail) {
      this.isEnteringNewEmail = newEmail
      this.$emit('new-email', newEmail)
    },
    sent() {
      this.discardEmail()
      this.$emit('sent')
    },
    clearSessionStorage() {
      sessionStorage.removeItem(`${this.$userInfo.sessionId}|${this.$route.meta.ekey}`)
      sessionStorage.removeItem(`${this.$userInfo.sessionId}|${this.$route.meta.pkey}`)
    },
    async resetEmailer() {
      let _emailer = this.retrieveEmailer()
      if (!_emailer) _emailer = StoreUtil.getLocalStorage(this.emailer.id, 'emailer')
      if (!_emailer) _emailer = _cloneDeep(this.getEmailer(this.emailer.id) || new Emailer())

      const updatedEmailer = _cloneDeep(_emailer)
      updatedEmailer.message = `\n\n${divider}\n${copyright.replace(/\[COMPANY\]/g, this.$userInfo.companyName)}`

      this.$emit('update:emailer', updatedEmailer)
      this.persistEmailer(updatedEmailer)
      //this.updateAttachmentList()
    },
    retrieveEmailer() {
      return JSON.parse(sessionStorage.getItem(`${this.$userInfo.sessionId}|${this.$route.meta.ekey}`))
    },
    persistEmailer(emailer = null) {
      const emailerToStore = emailer || this.emailer
      sessionStorage.setItem(`${this.$userInfo.sessionId}|${this.$route.meta.ekey}`, JSON.stringify(emailerToStore))
    },
    cleanEmailer() {
      StoreUtil.removeStorage(this.emailer.id, 'emailer')
      StoreUtil.removeLocalStorage(this.emailer.id, 'emailer')
      this.removeEmailer(this.emailer.id)
    }
  }
}
</script>

<style scoped>
.email-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border-radius: 6px;
  padding: 0rem;
}

.email-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.email-content {
  flex: 1;
}

.buttons {
  display: flex;
  gap: 0.5rem;
}

.modal-card {
  width: 100% !important;
  margin: 0;
}

.modal-card-head {
  width: 100% !important;
}

.modal-card-body {
  overflow-y: auto;
  padding: 1.5rem;
  width: 100% !important;
}

.dropdown-menu {
  min-width: 200px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  cursor: pointer;
}

.dropdown-item .icon {
  margin-right: 0.5rem;
}
</style>
